import type {
  IChatState,
  IErrorResponse,
  IResultResponse,
  IWebSocketMessage,
} from "./types";

import { Logger } from "utils/logger";

const connect = ({
  messageToSend,
  setError,
  onMessage,
}: {
  messageToSend: string;
  setError: IChatState["setError"];
  onMessage: (event: MessageEvent) => void;
}): { socketRef: WebSocket } => {
  const socket = new WebSocket(`wss://${window.location.host}/mcp/chat`);

  socket.addEventListener("open", (): void => {
    setError(null);
    socket.send(messageToSend);
  });

  socket.addEventListener("message", onMessage);

  socket.addEventListener("error", (): void => {
    Logger.error("WebSocket connection error", {
      messageToSend,
      readyState: socket.readyState,
    });
    setError(
      "Failed to connect to chat service. Please check your connection and try again.",
    );
  });

  return { socketRef: socket };
};

const getOnMessageHandler = ({
  setIsLoading,
  setError,
  setMessages,
  setPrompt,
}: {
  setIsLoading: IChatState["setIsLoading"];
  setError: IChatState["setError"];
  setMessages: IChatState["setMessages"];
  setPrompt: IChatState["setPrompt"];
}): ((event: MessageEvent) => void) => {
  const handleMessage = (event: MessageEvent): void => {
    setIsLoading(false);
    try {
      const messages = JSON.parse(event.data) as
        | IWebSocketMessage
        | IWebSocketMessage[];
      const messageArray = Array.isArray(messages) ? messages : [messages];

      messageArray.forEach((message): void => {
        switch (message.type) {
          case "result": {
            const content = message.content as unknown as IResultResponse;
            const { result, role, more_body: moreBody } = content;
            setIsLoading(moreBody);

            setMessages([{ content: result, role }]);
            break;
          }
          case "error": {
            const content = message.content as unknown as IErrorResponse;
            const { error } = content;
            setError(error);
            break;
          }
          case "reset_confirmation": {
            setMessages([], true);
            setPrompt(null);
            break;
          }
          default: {
            Logger.error("Unknown message type", {
              content: message.content,
              type: message.type,
            });
            setError("Unknown message error");
            break;
          }
        }
      });
    } catch (err) {
      Logger.error("Error parsing message", {
        error: err,
        rawData: event.data,
      });
      setError("Error parsing message");
    }
  };

  return handleMessage;
};

export { connect, getOnMessageHandler };
